import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Checklist as ChecklistIcon,
  Message as MessageIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material'
import { AppBar, Badge, Container, IconButton, Link, Toolbar, useTheme } from '@mui/material'
import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { useUnreadEntries } from '@src/data/api/chat-message/chat-message-api'
import { useChatMessageSignalR } from '@src/data/api/chat-message/chat-message-signalr-api'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { SUPPORTED_INSTANT_MESSAGE_PROGRAMS } from '@src/data/types/constants'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { useOptionalUser } from '@src/providers/UserProvider'
import { useCallback, useMemo, type JSX } from 'react'
import AppMenu from './components/AppMenu'
import CompanySelector from './components/CompanySelector'
import LanguagePicker from './components/LanguagePicker'
import FinancialEngineerMenu from './components/FinancialEngineerMenu'
import ReportMenu from './components/ReportMenu'
import UserMenu from './components/UserMenu'

interface Props {
  hasAlertBanner?: boolean
}

const Layout = ({ hasAlertBanner = false }: Props): JSX.Element | null => {
  const theme = useTheme()
  const { prefersDarkMode, setDarkMode, selectedCompany } = usePreferences()
  const user = useOptionalUser()
  const featureFlags = useFeatureFlags()

  const programOptions = useMemo(
    () =>
      selectedCompany
        ? getProgramsByCompanyId([selectedCompany]).filter((p) => SUPPORTED_INSTANT_MESSAGE_PROGRAMS.includes(p))
        : [],
    [selectedCompany],
  )
  const [unreadEntries] = useUnreadEntries(featureFlags.useInstantMessage)

  useChatMessageSignalR({ enabled: featureFlags.useInstantMessage })

  const totalUnread = useMemo(() => {
    const counts: Partial<Record<EFinancingProgram, number>> = {}
    for (const entry of unreadEntries) {
      const program = entry.financingProgramId as EFinancingProgram
      counts[program] = (counts[program] ?? 0) + 1
    }
    return programOptions.reduce((sum, program) => sum + (counts[program] ?? 0), 0)
  }, [unreadEntries, programOptions])

  const handleOpenChatDashboard = useCallback(() => {
    window.open('/chat-dashboard', '_blank')
  }, [])

  if (!user) {
    return null
  }

  return (
    <AppBar
      position="sticky"
      sx={[
        {
          backgroundColor: 'rgb(0, 0, 89)',
          color: 'white',
          mb: 3,
        },
        hasAlertBanner
          ? {
              top: 48,
              mt: '48px',
            }
          : {
              top: 0,
            },
      ]}
    >
      <Container maxWidth="xl" disableGutters>
        <Toolbar disableGutters>
          <div className="icebergBanner">
            <img
              src={selectedCompany === EFinancingCompany.IFinance ? '/images/ifinance-white-logo.svg' : '/images/iceberg-no-bg.png'}
              alt={selectedCompany === EFinancingCompany.IFinance ? 'iFinance' : 'Iceberg Finance'}
              style={{ width: '110px', height: '40px' }}
            />
          </div>
          <Link href="/">
            <IconButton>
              <ViewListIcon sx={{ color: '#ffffff' }} />
            </IconButton>
          </Link>
          <Link href="/creditTasksDashboard">
            <IconButton>
              <ChecklistIcon sx={{ color: '#ffffff' }} />
            </IconButton>
          </Link>
          {featureFlags.useInstantMessage && (
            <IconButton onClick={handleOpenChatDashboard}>
              <Badge badgeContent={totalUnread} color="error" max={99}>
                <MessageIcon sx={{ color: '#ffffff' }} />
              </Badge>
            </IconButton>
          )}
          <span style={{ flexGrow: 1 }}>&nbsp;</span>
          <CompanySelector />
          <AppMenu />
          <ReportMenu />
          <FinancialEngineerMenu />
          <UserMenu />
          <LanguagePicker />
          <IconButton onClick={() => setDarkMode(!prefersDarkMode)}>
            {theme.palette.mode === 'dark' ? (
              <Brightness7Icon sx={{ color: '#ffffff' }} />
            ) : (
              <Brightness4Icon sx={{ color: '#ffffff' }} />
            )}
          </IconButton>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default Layout
